import * as React from "react";
import { useMutation } from "convex/react";
import { toast } from "sonner";

import { api } from "$convex/convexApi";
import { Button } from "@/components/ui/button";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCargosWithDetails } from "@/hooks/useCargos";
import { useUsersForImpersonation } from "@/hooks/useUsers";
import { buildCustomerComboboxOptions } from "@/lib/customer-combobox";
import { toUserFacingErrorMessage } from "@/lib/errors";

type PrepaidItem = {
  id: string;
  cargoId: string;
  totalBags: string;
};

function createPrepaidItem(): PrepaidItem {
  return { id: crypto.randomUUID(), cargoId: "", totalBags: "" };
}

export function CreatePrepaidInventoryDialog() {
  const [open, setOpen] = React.useState(false);
  const [customerId, setCustomerId] = React.useState("");
  const [items, setItems] = React.useState<PrepaidItem[]>([createPrepaidItem()]);
  const [adminNotes, setAdminNotes] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);

  const users = useUsersForImpersonation();
  // Include archived cargos: prepaid coffee bought off-website often sits on an
  // archived/unlisted lot (Available 0) but is physically in the warehouse.
  const cargos = useCargosWithDetails(1000, true);
  const createPrepaid = useMutation(api.contractWorkflow.adminCreatePrepaidContract);

  const customerOptions = React.useMemo(
    () => buildCustomerComboboxOptions(users as any[] | undefined),
    [users],
  );

  // Prepaid is judged by physical bags not yet committed to anyone (warehouse - sold),
  // not spot availability — so list any cargo with free physical stock, archived or not.
  const eligibleCargos = React.useMemo<any[]>(
    () =>
      Array.isArray(cargos)
        ? cargos.filter(
            (c: any) =>
              Number(c.warehouseBags ?? 0) - Number(c.soldBags ?? 0) > 0,
          )
        : [],
    [cargos],
  );

  const cargoById = React.useMemo<Map<string, any>>(
    () => new Map(eligibleCargos.map((cargo: any) => [cargo._id, cargo])),
    [eligibleCargos],
  );

  const cargoOptions = React.useMemo(() => {
    return eligibleCargos.map((cargo: any) => {
      const freePhysical = Math.max(
        0,
        Number(cargo.warehouseBags ?? 0) - Number(cargo.soldBags ?? 0),
      );
      const originName = String(
        cargo.product?.origin?.name ?? cargo.product?.origin ?? "",
      ).trim();
      // Staff identify lots by ACI/contract number (cargo.contractId); OCI is secondary.
      const aciNumber = String(cargo.contractId ?? "").trim();
      const label = `${aciNumber || cargo.ociNumber || "No ACI"} - ${cargo.product?.name || "Unknown Product"}`;
      const sublabel = [
        originName,
        cargo.ociNumber ? `OCI ${cargo.ociNumber}` : "",
        `${freePhysical} in warehouse`,
      ]
        .filter(Boolean)
        .join(" · ");
      const searchText = [aciNumber, cargo.ociNumber, cargo.product?.name, originName]
        .filter(Boolean)
        .join(" ");
      return { value: cargo._id, label, sublabel: sublabel || undefined, searchText: searchText || undefined };
    });
  }, [eligibleCargos]);

  const resetForm = React.useCallback(() => {
    setCustomerId("");
    setItems([createPrepaidItem()]);
    setAdminNotes("");
  }, []);

  const updateItem = (id: string, patch: Partial<PrepaidItem>) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const handleSubmit = async () => {
    if (!customerId) {
      toast.error("Select a customer");
      return;
    }

    const seenCargoIds = new Set<string>();
    const normalizedItems: { cargoId: string; totalBags: number }[] = [];
    for (const item of items) {
      if (!item.cargoId) {
        toast.error("Select a coffee for each line");
        return;
      }
      if (seenCargoIds.has(item.cargoId)) {
        toast.error("Each coffee can only appear once");
        return;
      }
      seenCargoIds.add(item.cargoId);

      const bags = Number(item.totalBags);
      if (!Number.isInteger(bags) || bags <= 0) {
        toast.error("Enter a whole number of bags greater than 0 for each line");
        return;
      }

      const cargo = cargoById.get(item.cargoId);
      const freePhysical = cargo
        ? Math.max(0, Number(cargo.warehouseBags ?? 0) - Number(cargo.soldBags ?? 0))
        : 0;
      if (bags > freePhysical) {
        toast.error(`Only ${freePhysical} bags free in the warehouse for the selected coffee`);
        return;
      }

      normalizedItems.push({ cargoId: item.cargoId, totalBags: bags });
    }

    setSubmitting(true);
    try {
      const result = await createPrepaid({
        userId: customerId as any,
        items: normalizedItems as any,
        adminNotes: adminNotes.trim() || undefined,
      });
      toast.success(
        `Prepaid inventory created${result?.contractNumber ? ` (${result.contractNumber})` : ""}`,
      );
      resetForm();
      setOpen(false);
    } catch (error) {
      toast.error(toUserFacingErrorMessage(error, "Failed to create prepaid inventory"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetForm();
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Add Prepaid Inventory
        </Button>
      </DialogTrigger>
      {/* sm:max-w-2xl overrides DialogContent's default sm:max-w-lg (base max-w-2xl would be ignored at desktop). */}
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Add Prepaid Inventory</DialogTitle>
          <DialogDescription>
            Record coffee a customer already paid for off-website. Stock is reserved
            immediately and the customer draws it down at $0 (they only pay shipping).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Customer</Label>
            <ComboboxSelect
              options={customerOptions}
              value={customerId}
              onValueChange={setCustomerId}
              placeholder="Select a customer..."
              loading={users === undefined}
              disablePortal
            />
          </div>

          <div className="space-y-3">
            <Label>Coffee &amp; bags</Label>
            {items.map((item) => (
              <div key={item.id} className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <ComboboxSelect
                    options={cargoOptions}
                    value={item.cargoId}
                    onValueChange={(value) => updateItem(item.id, { cargoId: value })}
                    placeholder="Select coffee (cargo)..."
                    loading={cargos === undefined}
                    disablePortal
                  />
                </div>
                <div className="w-24">
                  <Input
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    placeholder="Bags"
                    value={item.totalBags}
                    onChange={(e) => updateItem(item.id, { totalBags: e.target.value })}
                  />
                </div>
                {items.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setItems((prev) => prev.filter((i) => i.id !== item.id))}
                  >
                    Remove
                  </Button>
                ) : null}
              </div>
            ))}
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setItems((prev) => [...prev, createPrepaidItem()])}
            >
              + Add another coffee
            </Button>
          </div>

          <div className="space-y-1.5">
            <Label>Notes (optional)</Label>
            <Textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              placeholder="e.g. Paid via wire on 2026-06-15, invoice #1234"
              rows={2}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? "Creating..." : "Create Prepaid Inventory"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
